import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Select, App, Collapse, Badge, Tooltip } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DatabaseOutlined,
  ImportOutlined,
  ExportOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import {
  ClickableStatCard,
  StatSection,
  DashboardSummary,
  PageTitle
} from '../../../components/common';
import { theme } from '../../../theme';
import { apiClient } from '../../../utils/api';

const { Option } = Select;

interface Producto {
  id: number;
  nombre: string;
  descripcion: string;
  tipo: number;
}

const EstadisticasCompras = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { message: messageApi } = App.useApp();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingProductos, setLoadingProductos] = useState(true);

  // Datos de compras
  const [datosCompras, setDatosCompras] = useState({
    aplicacionCompras: 0,
    ingresoCompras: 0,
    desmote: 0,
    desdeDesmote: 0,
    ingresoTotal: 0,
    aIngresar: 0,
    aplicacionVentas: 0,
    salidasVentas: 0,
    otrasSalidas: 0,
    totalSalidas: 0,
    aSalir: 0,
    stock: 0,
    disponibleStock: 0,
    disponible: 0
  });

  // Cargar productos tipo=2 (productos de desmote)
  useEffect(() => {
    const fetchProductos = async () => {
      if (!user) {
        console.log('Esperando usuario...');
        return;
      }

      setLoadingProductos(true);
      try {
        const token = await getAccessTokenSilently();

        if (!process.env.REACT_APP_BALANZA_URL) {
          console.error('REACT_APP_BALANZA_URL no está definida');
          messageApi.error('Error de configuración: URL de balanza no definida');
          return;
        }

        const urlProductos = `${process.env.REACT_APP_BALANZA_URL}/productos/`;
        console.log('Cargando productos desde:', urlProductos);
        console.log('REACT_APP_BALANZA_URL:', process.env.REACT_APP_BALANZA_URL);

        if (!urlProductos || urlProductos.includes('undefined')) {
          console.error('URL de productos inválida:', urlProductos);
          messageApi.error('Error de configuración: URL de productos inválida');
          return;
        }

        const response = await apiClient.get<Producto[]>(urlProductos, {
          token,
          timeout: 60000 // Aumentar timeout a 60 segundos para esta solicitud
        });
        console.log('Productos recibidos:', response.data);

        // Manejar diferentes estructuras de respuesta
        let productosArray: Producto[] = [];
        if (Array.isArray(response.data)) {
          productosArray = response.data;
        } else if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray((response.data as any).data)) {
          productosArray = (response.data as any).data;
        } else {
          console.error('Formato de respuesta inesperado:', response.data);
          messageApi.error('Error: Formato de respuesta inesperado');
          return;
        }

        const productosTipo2 = productosArray.filter((producto: Producto) => producto.tipo === 2);
        console.log('Productos tipo 2 filtrados:', productosTipo2);
        setProductos(productosTipo2);

        if (productosTipo2.length === 0) {
          messageApi.warning('No se encontraron productos tipo 2 (desmote)');
          return;
        }

        // Predeterminar Fibra
        const fibraProducto = productosTipo2.find((p: Producto) =>
          p.descripcion?.toUpperCase().includes('FIBRA') &&
          !p.descripcion?.toUpperCase().includes('FIBRILLA')
        );

        if (fibraProducto) {
          console.log('Seleccionando Fibra:', fibraProducto.id);
          setProductoSeleccionado(fibraProducto.id);
        } else if (productosTipo2.length > 0) {
          console.log('Seleccionando primer producto tipo 2:', productosTipo2[0].id);
          setProductoSeleccionado(productosTipo2[0].id);
        }
      } catch (error: any) {
        console.error('Error al cargar productos:', error);

        // Manejar errores de timeout específicamente
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          messageApi.error('La solicitud está tardando demasiado. Por favor, verifique la conexión con el servidor de balanza e intente nuevamente.');
        } else if (error.response) {
          // Error de respuesta del servidor
          const status = error.response.status;
          const errorMessage = error.response.data?.message || error.response.data?.detail || `Error ${status}`;
          messageApi.error(`Error al cargar productos: ${errorMessage}`);
        } else if (error.request) {
          // Error de red (sin respuesta)
          messageApi.error('No se pudo conectar con el servidor. Por favor, verifique su conexión e intente nuevamente.');
        } else {
          // Otro tipo de error
          messageApi.error('Error al cargar productos. Por favor, intente nuevamente.');
        }

        setProductos([]);
      } finally {
        setLoadingProductos(false);
      }
    };
    fetchProductos();
  }, [getAccessTokenSilently, user, messageApi]);

  // Cargar datos cuando cambia el producto seleccionado
  useEffect(() => {
    const fetchDatosCompras = async () => {
      if (!productoSeleccionado || !user?.id_empresa) {
        return;
      }

      setLoading(true);

      try {
        const token = await getAccessTokenSilently();
        const url = `${process.env.REACT_APP_ESTADISTICAS_URL}/datos_compras_fardos/${user.id_empresa}/${productoSeleccionado}/`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error al cargar datos de compras: ${response.status}`);
        }

        const data = await response.json();

        // Calcular métricas derivadas (lógica original de main)
        const aIngresar = data.aplicacionCompras - data.ingresoCompras;
        const aSalir = data.aplicacionVentas - data.salidasVentas;
        // Calcular "Stock" como: Ingreso Total - Total Salidas (lógica original de main)
        const stock = data.ingresoTotal - data.totalSalidas;
        // Calcular "Disponible Stock" como: Stock - Aplicación Ventas (lógica original de main)
        const disponibleStock = stock - data.aplicacionVentas;
        // Calcular "Disponible" como: Stock + A Ingresar - A Salir (lógica original de main)
        const disponible = stock + aIngresar - aSalir;

        setDatosCompras({
          ...data,
          aIngresar,
          aSalir,
          stock,
          disponibleStock,
          disponible
        });
      } catch (error) {
        console.error('Error al cargar datos de compras:', error);
        setDatosCompras({
          aplicacionCompras: 0,
          ingresoCompras: 0,
          desmote: 0,
          desdeDesmote: 0,
          ingresoTotal: 0,
          aIngresar: 0,
          aplicacionVentas: 0,
          salidasVentas: 0,
          otrasSalidas: 0,
          totalSalidas: 0,
          aSalir: 0,
          stock: 0,
          disponibleStock: 0,
          disponible: 0
        });
      } finally {
        setLoading(false);
      }
    };

    if (productoSeleccionado && user?.id_empresa) {
      fetchDatosCompras();
    }
  }, [productoSeleccionado, user?.id_empresa, getAccessTokenSilently]);

  const handleProductoChange = (value: number) => {
    setProductoSeleccionado(value);
  };

  // Resumen ejecutivo mejorado con badges, porcentajes y gráficos de tendencia
  const summaryMetrics = useMemo(() => [
    {
      title: 'Stock Actual',
      value: datosCompras.stock,
      suffix: 'fardos',
      color: theme.colors.stock,
      loading,
      badge: { text: 'Normal', color: theme.colors.stock },
      tooltip: 'Stock total en depósito (Ingreso Total - Total Salidas)',
      // Placeholder para tendencia de stock (últimos 7 días)
      trendData: [1150, 1180, 1200, 1190, 1210, 1230, datosCompras.stock > 0 ? datosCompras.stock : 1250]
    },
    {
      title: 'Disponible',
      value: datosCompras.disponible,
      suffix: 'fardos',
      color: theme.colors.purchases,
      loading,
      badge: { text: 'Normal', color: theme.colors.purchases },
      tooltip: 'Stock proyectado considerando pendientes (Stock + A Ingresar - A Salir)',
      // Porcentaje de margen: Disponible / Stock
      percentage: datosCompras.stock > 0
        ? Math.round((datosCompras.disponible / datosCompras.stock) * 100)
        : 0
    },
    {
      title: 'Pendiente Ingreso',
      value: datosCompras.aIngresar,
      suffix: 'fardos',
      color: datosCompras.aIngresar > 0 ? theme.colors.pending : theme.colors.purchases,
      prefix: datosCompras.aIngresar > 0 ? <ArrowUpOutlined /> : undefined,
      loading,
      badge: datosCompras.aIngresar > 0
        ? { text: 'Pendiente', color: theme.colors.pending }
        : { text: 'Completo', color: theme.colors.purchases },
      tooltip: 'Fardos aplicados a compras pero aún no ingresados (Aplicación Compras - Ingreso Compras)',
      // Porcentaje de aplicación: A Ingresar / Aplicación Compras
      percentage: datosCompras.aplicacionCompras > 0
        ? Math.round((datosCompras.aIngresar / datosCompras.aplicacionCompras) * 100)
        : 0
    },
    {
      title: 'Pendiente Salida',
      value: datosCompras.aSalir,
      suffix: 'fardos',
      color: datosCompras.aSalir > 0 ? theme.colors.pending : theme.colors.sales,
      prefix: datosCompras.aSalir > 0 ? <ArrowDownOutlined /> : undefined,
      loading,
      badge: datosCompras.aSalir > 0
        ? { text: 'Pendiente', color: theme.colors.pending }
        : { text: 'Completo', color: theme.colors.sales },
      tooltip: 'Fardos aplicados a ventas pero aún no despachados (Aplicación Ventas - Salidas Ventas)',
      // Porcentaje de aplicación: A Salir / Aplicación Ventas
      percentage: datosCompras.aplicacionVentas > 0
        ? Math.round((datosCompras.aSalir / datosCompras.aplicacionVentas) * 100)
        : 0
    }
  ], [
    datosCompras.stock,
    datosCompras.disponible,
    datosCompras.aIngresar,
    datosCompras.aSalir,
    datosCompras.aplicacionCompras,
    datosCompras.aplicacionVentas,
    loading
  ]);

  // Obtener nombre del producto seleccionado
  const productoNombre = productos.find(p => p.id === productoSeleccionado)?.descripcion || 'Producto';

  return (
    <div style={{ padding: '0' }}>
      {/* Título y Selector */}
      <div style={{ marginBottom: '24px' }}>
        <PageTitle
          level={3}
          style={{ fontSize: '22px', marginBottom: '16px' }}
        >
          Posicionamiento de Productos
        </PageTitle>

        <Row>
          <Col xs={24} md={12} lg={8}>
            <div style={{ padding: '10px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                fontSize: '14px',
                color: theme.colors.primaryDark
              }}>
                Producto:
              </label>
              <Select
                value={productoSeleccionado}
                onChange={handleProductoChange}
                placeholder="Seleccione un producto"
                loading={loadingProductos}
                style={{ width: '100%' }}
                size="large"
                showSearch
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {productos.map((producto) => (
                  <Option key={producto.id} value={producto.id}>
                    {producto.descripcion || producto.nombre}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
        </Row>
      </div>

      {/* Resumen Ejecutivo - Mejorado */}
      {productoSeleccionado && (
        <DashboardSummary
          title={`Resumen Ejecutivo - ${productoNombre}`}
          metrics={summaryMetrics}
        />
      )}

      {/* Secciones Expandibles - Métricas Detalladas */}
      {productoSeleccionado && (
        <Collapse
          defaultActiveKey={['compras']}
          style={{ marginBottom: '24px' }}
          items={[
            {
              key: 'compras',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ImportOutlined style={{ color: theme.colors.purchases }} />
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>Compras e Ingresos</span>
                  <Badge count={6} style={{ backgroundColor: theme.colors.purchases }} />
                </div>
              ),
              children: (
                <StatSection
                  title=""
                  headerColor={theme.colors.purchases}
                >
                  <ClickableStatCard
                    title="Aplicación Compras"
                    value={datosCompras.aplicacionCompras}
                    color={theme.colors.purchases}
                    suffix="fardos"
                    prefix={<ArrowUpOutlined />}
                    loading={loading}
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/aplicacion-compras?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Ingreso x Compras"
                    value={datosCompras.ingresoCompras}
                    color={theme.colors.purchases}
                    suffix="fardos"
                    prefix={<ArrowUpOutlined />}
                    loading={loading}
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/ingreso-compras?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Desmote"
                    value={datosCompras.desmote}
                    color={theme.colors.purchases}
                    suffix="fardos"
                    prefix={<ArrowUpOutlined />}
                    loading={loading}
                    tooltip="Fardos de producción propia (cliente NC='000000')"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/desmote?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Desde Desmote"
                    value={datosCompras.desdeDesmote}
                    color={theme.colors.purchases}
                    suffix="fardos"
                    prefix={<ArrowUpOutlined />}
                    loading={loading}
                    tooltip="Recepciones de servicios de desmote (Orden tipo G, cliente NC='000000')"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/desde-desmote?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Ingreso Total"
                    value={datosCompras.ingresoTotal}
                    color={theme.colors.purchases}
                    suffix="fardos"
                    prefix={<ArrowUpOutlined />}
                    loading={loading}
                    tooltip="Ingreso x Compras + Desmote + Desde Desmote"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/ingreso-total?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="A Ingresar"
                    value={datosCompras.aIngresar}
                    color={datosCompras.aIngresar > 0 ? theme.colors.pending : theme.colors.purchases}
                    suffix="fardos"
                    prefix={datosCompras.aIngresar > 0 ? <ArrowUpOutlined /> : undefined}
                    loading={loading}
                    subtitle={datosCompras.aIngresar > 0 ? "Pendiente de procesar" : "Todo procesado"}
                    tooltip="Aplicación Compras - Ingreso x Compras"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/a-ingresar?producto=${productoSeleccionado}` : undefined}
                  />
                </StatSection>
              )
            },
            {
              key: 'ventas',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ExportOutlined style={{ color: theme.colors.sales }} />
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>Ventas y Salidas</span>
                  <Badge count={5} style={{ backgroundColor: theme.colors.sales }} />
                </div>
              ),
              children: (
                <StatSection
                  title=""
                  headerColor={theme.colors.sales}
                >
                  <ClickableStatCard
                    title="Aplicación Ventas"
                    value={datosCompras.aplicacionVentas}
                    color={theme.colors.sales}
                    suffix="fardos"
                    prefix={<ArrowDownOutlined />}
                    loading={loading}
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/aplicacion-ventas?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Salidas x Ventas"
                    value={datosCompras.salidasVentas}
                    color={theme.colors.sales}
                    suffix="fardos"
                    prefix={<ArrowDownOutlined />}
                    loading={loading}
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/salidas-ventas?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Otras Salidas"
                    value={datosCompras.otrasSalidas}
                    color={theme.colors.sales}
                    suffix="fardos"
                    prefix={<ArrowDownOutlined />}
                    loading={loading}
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/otras-salidas?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Total Salidas"
                    value={datosCompras.totalSalidas}
                    color={theme.colors.sales}
                    suffix="fardos"
                    prefix={<ArrowDownOutlined />}
                    loading={loading}
                    tooltip="Salidas x Ventas + Otras Salidas"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/total-salidas?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="A Salir"
                    value={datosCompras.aSalir}
                    color={datosCompras.aSalir > 0 ? theme.colors.pending : theme.colors.sales}
                    suffix="fardos"
                    prefix={datosCompras.aSalir > 0 ? <ArrowDownOutlined /> : undefined}
                    loading={loading}
                    subtitle={datosCompras.aSalir > 0 ? "Pendiente de procesar" : "Todo procesado"}
                    tooltip="Aplicación Ventas - Salidas x Ventas"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/a-salir?producto=${productoSeleccionado}` : undefined}
                  />
                </StatSection>
              )
            },
            {
              key: 'stock',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatabaseOutlined style={{ color: theme.colors.stock }} />
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>Stock y Disponibilidad</span>
                  <Badge count={3} style={{ backgroundColor: theme.colors.stock }} />
                </div>
              ),
              children: (
                <StatSection
                  title=""
                  headerColor={theme.colors.stock}
                >
                  <ClickableStatCard
                    title="Stock"
                    value={datosCompras.stock}
                    color={theme.colors.stock}
                    suffix="fardos"
                    prefix={<InboxOutlined />}
                    loading={loading}
                    tooltip="Ingreso Total - Total Salidas"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/stock?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Disponible Stock"
                    value={datosCompras.disponibleStock}
                    color={theme.colors.stock}
                    suffix="fardos"
                    prefix={<InboxOutlined />}
                    loading={loading}
                    tooltip="Stock - Aplicación Ventas"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/disponible-stock?producto=${productoSeleccionado}` : undefined}
                  />
                  <ClickableStatCard
                    title="Disponible"
                    value={datosCompras.disponible}
                    color={theme.colors.stock}
                    suffix="fardos"
                    prefix={<InboxOutlined />}
                    loading={loading}
                    subtitle="Stock + A Ingresar - A Salir"
                    tooltip="Stock + A Ingresar - A Salir"
                    detailPath={productoSeleccionado ? `/reportes/compras/detalle/disponible?producto=${productoSeleccionado}` : undefined}
                  />
                </StatSection>
              )
            }
          ]}
        />
      )}
    </div>
  );
};

export default withAuthenticationRequired(EstadisticasCompras);
